import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE_URL } from '@/lib/config';

export type UserRole = 'user' | 'admin';

export interface UserProfile {
  id: string;
  displayName: string;
  email?: string;
  avatarUrl?: string;
  role: UserRole;
}

interface UserStoreState {
  users: UserProfile[];
  activeUserId: string | null;
  currentUser: UserProfile | null;
  lastActiveUserId: string | null;
  isLoading: boolean;
  error?: string;
  loadUsers: () => Promise<void>;
  createUser: (displayName: string, options?: { email?: string; makeAdmin?: boolean }) => Promise<UserProfile | null>;
  selectUser: (userId: string) => void;
  logout: () => void;
  deleteUser: (userId: string) => Promise<void>;
  updateUserRole: (userId: string, role: UserRole) => Promise<void>;
}

const API_BASE = API_BASE_URL;
const STORAGE_KEY = 'luma-skill-factory-user-store';
const DEFAULT_USER_HEADER = 'default-user';

async function request<T = any>(path: string, init?: RequestInit): Promise<T> {
  const userId = getActiveUserId() || DEFAULT_USER_HEADER;
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
      'X-Luma-User': userId
    }
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || `Request failed (${response.status})`);
  }

  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    throw new Error('Unexpected response format from server');
  }

  return response.json() as Promise<T>;
}

export const useUserStore = create<UserStoreState>()(
  persist(
    (set, get) => ({
      users: [],
      activeUserId: null,
      currentUser: null,
      lastActiveUserId: null,
      isLoading: false,

      loadUsers: async () => {
        set({ isLoading: true, error: undefined });
        try {
          const data = await request<{ users: UserProfile[] }>('/users');
          const { activeUserId, lastActiveUserId } = get();
          let resolvedActiveId: string | null = null;

          if (activeUserId && data.users.some((u) => u.id === activeUserId)) {
            resolvedActiveId = activeUserId;
          } else if (lastActiveUserId && data.users.some((u) => u.id === lastActiveUserId)) {
            // Keep last active in store but do not auto-login
            resolvedActiveId = null;
          }

          const currentUser = resolvedActiveId
            ? data.users.find((u) => u.id === resolvedActiveId) || null
            : null;

          set({
            users: data.users,
            isLoading: false,
            activeUserId: resolvedActiveId,
            currentUser
          });
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to load users', isLoading: false });
        }
      },

      createUser: async (displayName: string, options?: { email?: string; makeAdmin?: boolean }) => {
        const body = {
          displayName,
          email: options?.email,
          role: options?.makeAdmin ? 'admin' : undefined
        };
        const payload = await request<{ user: UserProfile }>('/users', {
          method: 'POST',
          body: JSON.stringify(body)
        });
        set((state) => ({
          users: [...state.users, payload.user]
        }));
        return payload.user;
      },

      selectUser: (userId: string) => {
        const { users } = get();
        const user = users.find((u) => u.id === userId);
        if (user) {
          set({ activeUserId: userId, currentUser: user, lastActiveUserId: userId });
        }
      },

      logout: () => {
        set({ activeUserId: null, currentUser: null });
      },

      deleteUser: async (userId: string) => {
        await request(`/users/${userId}`, { method: 'DELETE' });
        set((state) => {
          const remaining = state.users.filter((user) => user.id !== userId);
          const isCurrent = state.activeUserId === userId;
          const wasLastActive = state.lastActiveUserId === userId;
          return {
            users: remaining,
            activeUserId: isCurrent ? null : state.activeUserId,
            currentUser: isCurrent ? null : state.currentUser,
            lastActiveUserId: wasLastActive ? null : state.lastActiveUserId
          };
        });
      },

      updateUserRole: async (userId: string, role: UserRole) => {
        await request(`/users/${userId}/role`, {
          method: 'PATCH',
          body: JSON.stringify({ role })
        });
        set((state) => ({
          users: state.users.map((user) =>
            user.id === userId ? { ...user, role } : user
          ),
          currentUser:
            state.currentUser && state.currentUser.id === userId
              ? { ...state.currentUser, role }
              : state.currentUser
        }));
      }
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        activeUserId: state.activeUserId,
        lastActiveUserId: state.lastActiveUserId
      })
    }
  )
);

export const getActiveUserId = () => {
  try {
    return useUserStore.getState().activeUserId;
  } catch {
    return null;
  }
};

export function buildUserHeaders(existing?: HeadersInit): HeadersInit {
  const userId = getActiveUserId() || DEFAULT_USER_HEADER;
  return {
    ...(existing || {}),
    'X-Luma-User': userId
  };
}
